import React, { useEffect, useRef, useState } from "react";
import { api, getUser, setSession } from "../lib/api";
import { Input } from "../components/ui/input";
import PasswordInput from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Camera, Lock, User as UserIcon, Mail, Phone, Save, Loader2, Shield } from "lucide-react";

export default function ProfileSettings({ compact = false }) {
  const local = getUser();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", photo_url: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingP, setSavingP] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then(r => {
      setMe(r.data);
      setForm({
        name: r.data.name || "",
        email: r.data.email || "",
        mobile: r.data.mobile || "",
        photo_url: r.data.photo_url || "",
      });
    }).catch(() => toast.error("Failed to load profile"));
  }, []);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(s => ({ ...s, photo_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const saveProfile = async (e) => {
    e.preventDefault(); setSavingP(true);
    try {
      const r = await api.patch("/auth/me", form);
      setMe(r.data);
      // update cached session so nav/dashboard reflect immediately
      const merged = { ...(local || {}), name: r.data.name, email: r.data.email, mobile: r.data.mobile, photo_url: r.data.photo_url };
      setSession(localStorage.getItem("token"), merged);
      toast.success("Profile updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update"); }
    setSavingP(false);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.patch("/auth/me/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to change password"); }
    setSavingPw(false);
  };

  if (!me) return <div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const isAdmin = me.role === "admin";
  const initials = (me.name || me.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className={compact ? "" : "max-w-3xl mx-auto px-4 pt-8 pb-16"} data-testid="profile-settings">
      {!compact && (
        <div>
          <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> Settings</div>
          <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tight">Your Profile</h1>
          <p className="text-slate-600 mt-1 text-sm">Update your details, photo, and password.</p>
        </div>
      )}

      {/* Profile form */}
      <form onSubmit={saveProfile} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-5" data-testid="profile-form">
        <div className="flex items-center gap-5">
          <div className="relative">
            {form.photo_url ? (
              <img src={form.photo_url} alt="me" className="w-20 h-20 rounded-full object-cover ring-2 ring-white shadow-md" data-testid="profile-photo" />
            ) : (
              <div className={`w-20 h-20 rounded-full grid place-items-center text-white text-2xl font-black shadow-md ${isAdmin ? "bg-[#1e1b4b]" : "bg-gradient-to-br from-[#ea580c] to-amber-500"}`} data-testid="profile-avatar">{initials}</div>
            )}
            <button type="button" onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#ea580c] text-white grid place-items-center shadow-lg hover:bg-orange-700" data-testid="profile-photo-btn" title="Change photo">
              <Camera className="w-4 h-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
          </div>
          <div>
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${isAdmin ? "bg-[#1e1b4b] text-white" : "bg-amber-100 text-amber-800"}`}>
              {isAdmin && <Shield className="w-3 h-3" />} {me.role}
            </div>
            <div className="mt-1 text-sm text-slate-500">Member since {(me.created_at || "").slice(0, 10)}</div>
            {form.photo_url && (
              <button type="button" onClick={() => setForm(s => ({ ...s, photo_url: "" }))} className="mt-1 text-xs text-rose-600 hover:underline" data-testid="profile-photo-remove">Remove photo</button>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> Full Name</Label>
            <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</Label>
            <Input required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="profile-email" />
          </div>
          <div className="md:col-span-2">
            <Label className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Mobile</Label>
            <Input required inputMode="numeric" value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} data-testid="profile-mobile" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button disabled={savingP} type="submit" className="bg-[#ea580c] hover:bg-orange-700 inline-flex items-center gap-1" data-testid="profile-save">
            {savingP ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </Button>
        </div>
      </form>

      {/* Password reset */}
      <form onSubmit={savePassword} className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4" data-testid="password-form">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#1e1b4b]" />
          <div className="font-display font-bold text-[#1e1b4b]">Change Password</div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Current password</Label>
            <PasswordInput required value={pwd.current_password} onChange={e => setPwd({ ...pwd, current_password: e.target.value })} data-testid="pwd-current" />
          </div>
          <div>
            <Label>New password</Label>
            <PasswordInput required value={pwd.new_password} onChange={e => setPwd({ ...pwd, new_password: e.target.value })} data-testid="pwd-new" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <PasswordInput required value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button disabled={savingPw} type="submit" className="bg-[#1e1b4b] hover:bg-[#14205a] inline-flex items-center gap-1" data-testid="pwd-save">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Update password
          </Button>
        </div>
      </form>
    </div>
  );
}
